import { useState, useCallback, useEffect } from "react"
import { isValidEmail } from "@/utils/StringUtils"

export const useEmailInput = (
  initialEmail: string = ""
): {
  email: string
  setEmail: (value: ((prevState: string) => string) | string) => void
  handleEmailChange: (newEmail: string) => void
  validateEmail: () => boolean
  emailError: string
  isEmailValid: boolean
} => {
  const [email, setEmail] = useState<string>(initialEmail)
  const [emailError, setEmailError] = useState<string>("")
  const [isEmailValid, setIsEmailValid] = useState<boolean>(
    isValidEmail(initialEmail)
  )

  useEffect(() => {
    validateEmail()
  }, [email])

  // Pass the value to validate explicitly
  const doValidateEmail = useCallback((valueToCheck: string): boolean => {
    const valid = isValidEmail(valueToCheck)
    setEmailError(valid ? "" : "Invalid email format.")
    setIsEmailValid(valid)
    return valid
  }, [])

  const validateEmail = useCallback(() => {
    return doValidateEmail(email)
  }, [email, doValidateEmail])

  // The onChange handler calls validation with the *new* email value
  const handleEmailChange = useCallback(
    (newEmail: string) => {
      setEmail(newEmail)
      doValidateEmail(newEmail) // Validate immediately with fresh input
    },
    [doValidateEmail]
  )

  return {
    email,
    setEmail,
    handleEmailChange,
    validateEmail,
    emailError,
    isEmailValid,
  }
}
